import { computed, inject, reactive, ref } from 'vue';
import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
import { FeatureService } from '@shared/directives/Feature.service';
import { JourneyStatus, NodeType, NodeTaskVariation } from '@journeys/Enums';
import vm, { JourneyNode } from '@journeys/Journeys.viewmodel';
import { stepList } from '@journeys/utils/journeySerialization';
import { SegmentDefinition } from '@cwd/Views/Segments/interfaces';
import { IssueStatus } from '@cwd/Views/Issues/interfaces';
import { TextMessageStatus } from '@cwd/Views/TextMessages/TextMessagesTypes.d';

type CategoryWithWindow = {
    CategoryId: string;
    AllowedDays: number | null;
    AllowedFromTime: string | null;
    AllowedToTime: string | null;
    AllowedTimezone: string | null;
};

export const categories = ref<CategoryWithWindow[]>([]);

const DAY_BITS = [
    { bit: 2, label: () => $Resources.CronWeekdayMON },
    { bit: 4, label: () => $Resources.CronWeekdayTUE },
    { bit: 8, label: () => $Resources.CronWeekdayWED },
    { bit: 16, label: () => $Resources.CronWeekdayTHU },
    { bit: 32, label: () => $Resources.CronWeekdayFRI },
    { bit: 64, label: () => $Resources.CronWeekdaySAT },
    { bit: 1, label: () => $Resources.CronWeekdaySUN },
];

const formatSendWindow = (cat: CategoryWithWindow): string => {
    const days = DAY_BITS.filter((d) => (cat.AllowedDays! & d.bit) !== 0)
        .map((d) => d.label())
        .join(', ');
    const from = cat.AllowedFromTime?.substring(0, 5) ?? '';
    const to = cat.AllowedToTime?.substring(0, 5) ?? '';
    return `${days}, ${from}–${to} (${cat.AllowedTimezone})`;
};

const { activeJourney, editor, nav, viewport, toast, account } = vm;

export const state = reactive({
    reactive_exp: true,
    openExecutionHistory: false,
    computingToasts: false,
});

export const setupToasts = () => {
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const conditionToasts = computed(() => {
        state.computingToasts = true;
        const toasts = [];

        if (!featureAccess.hasFeature('Automation.Journeys')) {
            toasts.push({
                key: 'missing_feature',
                type: 'error',
                message: {
                    head: $Resources.JourneyMissingFeatureHeading,
                    body: $Resources.JourneyMissingFeatureBody,
                    break: true,
                },
                node_id: null,
            });
        }

        if (!activeJourney.startingNode)
            toasts.push({
                key: 'start_node_err',
                type: 'error',
                message: {
                    head: $Resources.JourneyAlertDefineStartHeading,
                    body: $Resources.JourneyAlertDefineStartBody,
                    break: true,
                },
                node_id: null,
            });

        if (!activeJourney.scheduleController.Scheduled)
            toasts.push({
                key: 'start_date_err',
                type: 'error',
                message: {
                    head: $Resources.JourneyAlertNotScheduledHeading,
                    body: $Resources.JourneyAlertNotScheduledBody,
                    break: true,
                },
                node_id: null,
            });

        // Check if all nodes are reachable from the starting node
        if (activeJourney.startingNode) {
            const nodes = activeJourney.nodes;
            const lines = activeJourney.lines;
            const reachableNodes = new Set<number>();
            const queue: number[] = [activeJourney.startingNode];
            reachableNodes.add(activeJourney.startingNode);

            // BFS traversal from starting node
            while (queue.length > 0) {
                const currentNodeId = queue.shift()!;
                const currentNode = nodes[currentNodeId] as JourneyNode;

                if (!currentNode || currentNode.data.type === NodeType.Text) continue;

                // Check outgoing connections (lnto)
                if (currentNode.lnto) {
                    for (const lineId in currentNode.lnto) {
                        const line = lines[lineId];
                        if (line && line.to !== null && !reachableNodes.has(line.to)) {
                            reachableNodes.add(line.to);
                            queue.push(line.to);
                        }
                    }
                }
            }

            // Check if any non-Text node is not reachable
            for (const key in nodes) {
                const node = nodes[key];
                if (node.data.type === NodeType.Text) continue;

                const nodeId = (node as JourneyNode).id;
                if (!reachableNodes.has(nodeId)) {
                    toasts.push({
                        key: `unreachable_node_${nodeId}`,
                        type: 'warning',
                        message: {
                            head: `'${(node as JourneyNode).data.title || $Resources.NoNamePlaceholder}' ` + $Resources.JourneyAlertNotReachableNodeHeading,
                            body: $Resources.JourneyAlertNotReachableNodeBody,
                            break: true,
                        },
                        node_id: nodeId,
                    });
                    break; // Only show one error toast for unreachable nodes
                }
            }
        }

        // Check for lines that are not properly connected to nodes
        let lines = activeJourney.lines;
        let nodes = activeJourney.nodes;

        for (let key in lines) {
            let line = lines[key];
            const lineId = line.id;
            let isDisconnected = false;

            // Check if line has valid node references
            if (line.to === null || line.fr === null) {
                isDisconnected = true;
            } else {
                // Check if the 'from' node exists and has this line in its outgoing connections
                const fromNode = nodes[line.fr] as JourneyNode;
                if (!fromNode || !fromNode.lnto || !(lineId in fromNode.lnto)) {
                    isDisconnected = true;
                }

                // Check if the 'to' node exists and has this line in its incoming connections
                const toNode = nodes[line.to] as JourneyNode;
                if (!toNode || !toNode.lnfr || !(lineId in toNode.lnfr)) {
                    isDisconnected = true;
                }
            }

            if (isDisconnected) {
                toasts.push({
                    key: 'disconnected_line_err',
                    type: 'warning',
                    message: {
                        head: $Resources.JourneyAlertDisconnectedLineHeading,
                        body: $Resources.JourneyAlertDisconnectedLineBody,
                        break: true,
                    },
                    node_id: null,
                });
                break; // Only show one error toast for disconnected lines
            }
        }

        for (let key in nodes) {
            let node = nodes[key];
            if (node.data.type === NodeType.Text) continue;

            if (node.data.type === NodeType.Segment) {
                if (node.data.ListIds?.length === 0 && node.data.SegmentIds?.length === 0) {
                    toasts.push({
                        key: 'start_segment_null',
                        type: 'error',
                        message: {
                            head: `'${node.data.title || $Resources.NoNamePlaceholder}' ` + $Resources.JourneyAlertNodeMissingListOrSegmentHeading,
                            body: $Resources.JourneyAlertNodeMissingListOrSegmentBody,
                            break: true,
                        },
                        node_id: node.id,
                    });
                }
            }
            if (node.data.type === NodeType.Task) {
                const isTagNode = node.data.var === NodeTaskVariation.Tag;
                const isContactListNode = node.data.var === NodeTaskVariation.ContactList;
                const isMissingEmailAndSms = !node.data.IssueId && !node.data.SmsMessageId;
                const shouldWarnMissingContent = !isTagNode && !isContactListNode && isMissingEmailAndSms;

                if (shouldWarnMissingContent) {
                    toasts.push({
                        key: `${node.id}_task_node_issue_null`,
                        type: isConnectedToStartNode(node.id) ? 'error' : 'warning',
                        message: {
                            head: `'${node.data.title || $Resources.NoNamePlaceholder}' ` + $Resources.JourneyAlertNodeMissingIssueOrSmsHeading,
                            body: $Resources.JourneyAlertNodeMissingIssueOrSmsBody,
                        },
                        node_id: node.id,
                    });
                }

                if (node.data.var === NodeTaskVariation.Email && node.data.IssueId) {
                    const issue = account.listings.getListing('/issues/listing', node.data.IssueId);
                    if (issue?.Status === IssueStatus.Template) {
                        toasts.push({
                            key: `${node.id}_task_node_issue_is_template`,
                            type: 'error',
                            message: {
                                head: `'${node.data.title || $Resources.NoNamePlaceholder}'`,
                                body: $Resources.JourneyAlertNodeConnectedToTemplate,
                            },
                            node_id: node.id,
                        });
                    }
                    if (issue?.Journey && issue.Journey.JourneyId !== activeJourney.JourneyId) {
                        toasts.push({
                            key: `${node.id}_task_node_issue_owned_by_other_journey`,
                            type: 'error',
                            message: {
                                head: `'${node.data.title || $Resources.NoNamePlaceholder}'`,
                                body: $Resources.JourneyAlertIssueOwnedByOtherJourney,
                            },
                            node_id: node.id,
                        });
                    }
                    if (issue?.Event) {
                        toasts.push({
                            key: `${node.id}_task_node_issue_owned_by_event`,
                            type: 'error',
                            message: {
                                head: `'${node.data.title || $Resources.NoNamePlaceholder}'`,
                                body: $Resources.JourneyAlertIssueOwnedByEvent,
                            },
                            node_id: node.id,
                        });
                    }
                    if (issue?.Category) {
                        const cat = categories.value.find((c) => c.CategoryId === issue.Category.CategoryId);
                        if (cat?.AllowedDays != null) {
                            toasts.push({
                                key: `${node.id}_issue_category_window`,
                                type: 'warning',
                                message: {
                                    head: `'${node.data.title || $Resources.NoNamePlaceholder}'`,
                                    body: $Resources.CategoryRestrictedSendTimesAlert.replace('{CategoryName}', issue.Category.Name).replace(
                                        '{formatSendWindow}',
                                        formatSendWindow(cat)
                                    ),
                                },
                                node_id: node.id,
                            });
                        }
                    }
                }

                if (node.data.var === NodeTaskVariation.Sms && node.data.SmsMessageId) {
                    const sms = account.listings.getListing('/textmessages/listing', node.data.SmsMessageId);
                    if (sms?.Status === TextMessageStatus.Template) {
                        toasts.push({
                            key: `${node.id}_task_node_sms_is_template`,
                            type: 'error',
                            message: {
                                head: `'${node.data.title || $Resources.NoNamePlaceholder}'`,
                                body: $Resources.JourneyAlertNodeConnectedToTemplateBody,
                            },
                            node_id: node.id,
                        });
                    }
                    if (sms?.Journey && sms.Journey.JourneyId !== activeJourney.JourneyId) {
                        toasts.push({
                            key: `${node.id}_task_node_sms_owned_by_other_journey`,
                            type: 'error',
                            message: {
                                head: `'${node.data.title || $Resources.NoNamePlaceholder}'`,
                                body: $Resources.JourneyAlertIssueOwnedByOtherJourney,
                            },
                            node_id: node.id,
                        });
                    }
                    if (sms?.Category) {
                        const cat = categories.value.find((c) => c.CategoryId === sms.Category.CategoryId);
                        if (cat?.AllowedDays != null) {
                            toasts.push({
                                key: `${node.id}_sms_category_window`,
                                type: 'warning',
                                message: {
                                    head: `'${node.data.title || $Resources.NoNamePlaceholder}'`,
                                    body: $Resources.CategoryRestrictedSendTimesAlert.replace('{CategoryName}', sms.Category.Name).replace(
                                        '{formatSendWindow}',
                                        formatSendWindow(cat)
                                    ),
                                },
                                node_id: node.id,
                            });
                        }
                    }
                }
            }
        }

        stepList.value?.forEach((step: any) => {
            const isInvalid = collectIsValid(step.data.SegmentDefinition).includes(false);
            if (isInvalid) {
                toasts.push({
                    key: `${step.data.Name}_step_invalid`,
                    type: 'error',
                    message: {
                        head: $Resources.JourneyInvalidSegmentHead,
                        body: $Resources.JourneyInvalidSegmentBody,
                    },
                    node_id: step.nodeId,
                });
            }
        });

        state.computingToasts = false;
        return toasts;
    });

    return {
        conditionToasts,
    };
};

const isConnectedToStartNode = (nodeId: number): boolean => {
    if (!activeJourney.startingNode) return false;
    if (nodeId === activeJourney.startingNode) return true;

    const nodes = activeJourney.nodes;
    const lines = activeJourney.lines;
    const visited = new Set<number>();
    const queue: number[] = [activeJourney.startingNode];
    visited.add(activeJourney.startingNode);

    // BFS traversal from starting node
    while (queue.length > 0) {
        const currentNodeId = queue.shift()!;
        const currentNode = nodes[currentNodeId] as JourneyNode;

        if (!currentNode || currentNode.data.type === NodeType.Text) continue;

        // Check outgoing connections (lnto)
        if (currentNode.lnto) {
            for (const lineId in currentNode.lnto) {
                const line = lines[lineId];
                if (line && line.to !== null) {
                    if (line.to === nodeId) return true;
                    if (!visited.has(line.to)) {
                        visited.add(line.to);
                        queue.push(line.to);
                    }
                }
            }
        }
    }

    return false;
};

const collectIsValid = (segmentDefinition: SegmentDefinition | undefined) => {
    if (!segmentDefinition) return [];
    let isValidValues: boolean[] = [];

    if (segmentDefinition.Type !== 'SegmentGroup') {
        isValidValues.push(segmentDefinition.IsValid === undefined ? true : (segmentDefinition.IsValid ?? false));
    }

    if (segmentDefinition.Type === 'SegmentGroup' && segmentDefinition.Segments?.length) {
        for (const subSegment of segmentDefinition.Segments) {
            isValidValues = isValidValues.concat(collectIsValid(subSegment));
        }
    }

    return isValidValues;
};

export const inspectToastTarget = (id: number) => {
    editor.state.nodeSettingsTab = 'data';
    editor.state.showNodeMenu = true;
    editor.tools.sel_keyObj = null;
    editor.tools.sel_nodes = {};
    editor.tools.sel_nodes[id] = activeJourney.nodes[id];
    editor.tools.sel_points = {};
    editor.tools.sel_lines = {};
    editor.tools.highlight = {};

    // not uber precise .. 120 is for half of propWindow
    nav.x = editor.tools.sel_nodes[id].x + viewport.computed.value.raw_pws / 2 + editor.tools.sel_nodes[id].w / 2;
    nav.y = editor.tools.sel_nodes[id].y + editor.tools.sel_nodes[id].h / 2;
};

const disableNodeSchedules = async (callback: <A, T>(...args: A[] | any) => T | any, action: 'stop' | 'end' = 'stop') => {
    const steps: any = Object.values(activeJourney.steps);
    const objectIds = Array(steps.length);

    for (let i = 0; i < steps.length; i++) {
        let node = activeJourney.nodes[steps[i].nodeId] as JourneyNode;

        if (!node || !node.data.Schedule) continue;

        node.data.Schedule!.Scheduled = null;
        node.data.Schedule!.TaskStatus = action === 'stop' ? 'Aborted' : 'Ended';
        objectIds[i] = steps[i].data.IssueId || steps[i].data.SmsMessageId;
    }

    await callback<string[], void>(objectIds.filter(Boolean));

    if (action === 'stop') {
        toast.emit('note', {
            pos: 'top-left',
            type: 'success',
            message: { head: $Resources.JourneyStoppedAlertHeading, body: $Resources.JourneyStoppedAlertBody, break: true },
        });
    } else {
        toast.emit('note', {
            pos: 'top-left',
            type: 'success',
            message: { head: $Resources.JourneyEndedAlertHeading, body: $Resources.JourneyEndedAlertBody, break: true },
        });
    }

    editor.events.emit('savecurrent');
};

export const handleResponse = async (data: any, callback: <A, T>(...args: A[] | any) => T | any, action: 'stop' | 'end' = 'stop') => {
    if (data === null) return disableNodeSchedules(callback, action);

    activeJourney.Status = JourneyStatus.Active;
    const objectIds = Array(data.length);

    for (let i = 0; i < data.length; i++) {
        const node = activeJourney.nodes[data[i].NodeId] as JourneyNode;

        node.data.Schedule = data[i].Schedule;
        node.data.TaskId ||= data[i].TaskId;

        if (data[i].IssueId) objectIds[i] = node.data.IssueId = data[i].IssueId;
        else if (data[i].SmsMessageId) objectIds[i] = node.data.SmsMessageId = data[i].SmsMessageId;

        // this node's step just got saved server-side under its (possibly client-generated)
        // SegmentId - it now backs a real Segment, so the "go to segment" link may show for it
        if (node.data.SegmentId) activeJourney.publishedStepNodeIds.add(node.id);
    }

    await callback<string[], void>(objectIds.filter(Boolean));

    toast.emit('note', {
        pos: 'top-left',
        type: 'success',
        message: { head: $Resources.JourneyScheduledAlertHeading, body: $Resources.JourneyScheduledAlertBody, break: true },
    });

    editor.events.emit('savecurrent');
};
